import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { ButterPage } from 'lib/butter/types';
import {
  City,
  Consumable,
  ConsumableCategory,
  Country,
  CustomerProduct,
  Market,
  MarketTaskTypeVendor,
  StartupResponse,
  Updatable,
  updateInList
} from 'shared';

import { AppLocation } from './types';

type AppState = {
  countries: Country[];
  anonymousId: string | null;
  cmsPages: ButterPage[];
  locations: AppLocation[];
  state: State;
  cities?: City[];
  markets: Market[];
  entranceTypes: string[];
  accessMethodTypes: string[];
  marketTaskTypeVendors: MarketTaskTypeVendor[] | null;
  googleOAuthLoaded: boolean;
  googleMapsLoaded: boolean;
  products: CustomerProduct[];
  consumableCategories: ConsumableCategory[];
  consumables: Consumable[];
  nextConsumablesOrdersDeliveryDate: string | null;
};

type State = 'loading' | 'ready';

const initialState: AppState = {
  countries: [],
  markets: [],
  entranceTypes: [],
  accessMethodTypes: [],
  marketTaskTypeVendors: null,
  anonymousId: null,
  cmsPages: [],
  locations: [],
  state: 'loading',
  googleOAuthLoaded: false,
  googleMapsLoaded: false,
  products: [],
  consumableCategories: [],
  consumables: [],
  nextConsumablesOrdersDeliveryDate: null
};

const appSlice = createSlice({
  name: 'App',
  initialState,
  reducers: {
    setStartup: (
      state,
      {
        payload: {
          countries,
          cities,
          markets,
          next_consumables_orders_delivery_date,
          entrance_types,
          access_method_types,
          products,
          consumable_categories,
          consumables,
          market_task_type_vendors
        }
      }: PayloadAction<StartupResponse>
    ) => {
      state.countries = countries;
      state.cities = cities;
      state.markets = markets;
      state.entranceTypes = [...(entrance_types ?? [])];
      state.accessMethodTypes = [...(access_method_types ?? [])];
      state.products = products;
      state.consumableCategories = consumable_categories;
      state.consumables = consumables;
      state.marketTaskTypeVendors = market_task_type_vendors;
      state.nextConsumablesOrdersDeliveryDate = next_consumables_orders_delivery_date;
    },
    setAnonymousId: (state, { payload }: PayloadAction<string | null>) => {
      state.anonymousId = payload;
    },
    setCmsPages: (state, { payload }: PayloadAction<ButterPage[]>) => {
      state.cmsPages = payload;
    },
    setButterLocations: (state, { payload }: PayloadAction<AppLocation[]>) => {
      state.locations = payload;
    },
    setState: (state, { payload }: PayloadAction<State>) => {
      state.state = payload;
    },
    updateButterLocationById: (state, { payload }: PayloadAction<Updatable<AppLocation>>) => {
      updateInList(state.locations, payload);
    },
    setGoogleOAuthLoaded: (state, { payload }: PayloadAction<boolean>) => {
      state.googleOAuthLoaded = payload;
    },
    setGoogleMapsLoaded: (state, { payload }: PayloadAction<boolean>) => {
      state.googleMapsLoaded = payload;
    }
  }
});

export const { actions } = appSlice;

export default appSlice.reducer;
